import { get } from 'app/utils/lo/lo';
import { loadWorkspaceClassResourcesLazyQuery } from 'graphql/workspaces/loadWorkspaceLazyResourcesQueries';
import { searchWorkspaceClassResourcesQuery, workspaceTeamMembersQuery } from 'graphql/workspaces/searchWorkspaceResourcesQueries';
import { ENTITY_BASED_TYPES, getEntityType } from 'app/config/workspaceResourceTypesConfig';

/**
 * Re-export for backwards compatibility
 */
export { ENTITY_BASED_TYPES };

/**
 * Workspace-based query configurations for specific resource types
 */
export const WORKSPACE_RESOURCES_QUERY_CONFIG = {
    class: {
        lazy: loadWorkspaceClassResourcesLazyQuery,
        search: searchWorkspaceClassResourcesQuery,
    },
    team: {
        search: workspaceTeamMembersQuery,
    },
};

/**
 * Get the appropriate query for a resource type
 * @param {string} resourceType - The resource type ('class', 'entity', 'team')
 * @param {Object} queries - Object containing query mappings
 * @returns {Object} The corresponding query
 */
export const getResourceQuery = (resourceType, queries) => {
    return queries[resourceType];
};

/**
 * Get the appropriate search query for a resource type
 * @param {string} resourceType - The resource type ('class', 'entity', 'team')
 * @param {Object} queries - Object containing search query mappings
 * @returns {Object} The corresponding search query
 */
export const getResourceSearchQuery = (resourceType, queries) => {
    return queries[resourceType];
};

/**
 * Transform resource data based on resource type
 * Extracts the nested object and merges role if present
 * @param {Array} resources - Array of resource objects
 * @param {string} resourceType - The resource type ('class', 'entity', 'team')
 * @returns {Array} Transformed array of resources
 */
export const transformResourceRecords = (resources, resourceType) => {
    if (resourceType === 'class') {
        return resources.map(({ class: cls, role }) => ({ ...cls, role })).filter(Boolean);
    }
    if (resourceType === 'team') {
        return resources.map(({ team, role }) => ({ ...team, role })).filter(Boolean);
    }
    return resources.map(({ entity }) => entity).filter(Boolean);
};

/**
 * Extract records from search response
 * @param {Object} response - GraphQL response object
 * @param {string} resourceType - The resource type ('class', 'entity', 'team')
 * @returns {Array} Array of extracted records
 */
export const extractSearchRecords = (response, resourceType) => {
    const data = get(response, 'data', []);
    const records = transformResourceRecords(data, resourceType);
    const countData = get(response, 'count', []);
    const count = countData.length;

    return { records, count };
};

/**
 * Extract records and count from lazy load response
 * All queries now return standardized 'workspace.data' and 'workspace.count' fields
 * For entity-based types (task, process, script), data is directly in response.data
 * @param {Object} response - GraphQL response object
 * @param {string} resourceType - The resource type ('class', 'entity', 'team', 'task', 'process', 'script')
 * @returns {Object} Object containing records and count { records: Array, count: number }
 */
export const extractLazyLoadData = (response, resourceType) => {
    // For entity-based resource types (task, process, script), extract from root data
    if (ENTITY_BASED_TYPES.includes(resourceType)) {
        const resources = get(response, 'data', []);
        const countData = get(response, 'count', []);
        const records = transformResourceRecords(resources, resourceType);
        const count = countData.length;
        return { records, count };
    }

    // For workspace-based resource types (class, entity, team)
    const workspace = get(response, 'workspace', {});
    const resources = get(workspace, 'data', []);
    const count = get(workspace, 'count', 0);
    const records = transformResourceRecords(resources, resourceType);

    return { records, count };
};

/**
 * Get configuration for loading workspace resource IDs
 * @param {string} resourceType - The resource type ('class', 'primaryClass', 'team', 'task', 'process', 'script')
 * @param {Object} queries - Object containing query mappings
 * @returns {Object} Configuration object with query, resourceKey, extractor, and entityType
 */
export const getResourceIdConfig = (resourceType, queries) => {
    // Workspace-based resource types with specific queries
    const workspaceResourceConfigs = {
        class: {
            query: queries.loadWorkspaceClassResourceIdsQuery,
            resourceKey: 'workspace.classResources',
            extractor: (resources) => resources.map(({ class: cls }) => cls?.id).filter(Boolean),
        },
        primaryClass: {
            query: queries.loadWorkspaceEntityResourceIdsQuery,
            resourceKey: 'workspace.entityResources',
            extractor: (resources) => resources.map(({ entity }) => entity?.id).filter(Boolean),
        },
        team: {
            query: queries.loadWorkspaceTeamIdsQuery,
            resourceKey: 'workspace.teams',
            extractor: (resources) => resources.map(({ team }) => team?.id).filter(Boolean),
        },
    };

    // Check if it's a workspace-based resource
    if (workspaceResourceConfigs[resourceType]) {
        return workspaceResourceConfigs[resourceType];
    }

    // Entity-based resource types use generic query with entityType from centralized config
    return {
        query: queries.loadWorkspaceEntityResourceIdsByTypeQuery,
        resourceKey: 'workspaceEntityResources',
        entityType: getEntityType(resourceType), // Get entity type from centralized config
        extractor: (resources) => resources.map(({ entity }) => entity?.id).filter(Boolean),
    };
};

/**
 * Normalizes the workspace payload returned by the GraphQL query.
 *
 * Purpose:
 * - The API returns `teamsCount` including the private team.
 * - We subtract 1 to exclude the private team from the visible count.
 *
 * Safety:
 * - If `teamsCount` is undefined or null, it defaults to 1.
 * - Ensures the final count can never go below zero.
 *
 * @param {Object} payload - Raw workspace response from the server.
 * @param {number} payload.teamsCount - Total teams including the private one.
 * @returns {Object} A new payload object with an adjusted `teamsCount`.
 */
export const normalizeWorkspacePayload = (payload) => {
    const { teamsCount = 1, ...rest } = payload;

    return {
        ...rest,
        teamsCount: Math.max(teamsCount - 1, 0),
    };
};

/**
 * Build filterBy array for entity-based resource types
 * @param {string} workspaceId - The workspace ID
 * @param {string} entityType - The entity type ('task', 'process', 'system_task', 'system_process', 'script', etc.)
 * @param {string} searchQuery - Optional search query
 * @returns {Array} filterBy array for GraphQL query
 */
export const buildEntityResourceFilterBy = (workspaceId, entityType, searchQuery = null) => {
    const entityTypeFilter = { field: 'entity.type', op: '=', value: entityType };
    const resourcesWithoutPermissionsFilter = {
        field: 'entity.type',
        op: 'not in',
        value: ['permission', 'system_setting'],
    };
    const filterBy = [
        { field: 'workspace.id', op: '=', value: workspaceId },
        ['entity', 'primaryClass'].includes(entityType) ? resourcesWithoutPermissionsFilter : entityTypeFilter,
    ];

    if (searchQuery) {
        filterBy.push({
            or: [
                { field: 'entity.id', op: 'startsWith', value: searchQuery, cast: 'text' },
                { field: 'entity.name', op: 'contains', value: searchQuery },
            ],
        });
    }

    return filterBy;
};
